import * as layout from '../../../components/markLayout.module.css';
import Table from '@hero/ui-kit/tables/Table';
import { formatDueAt } from './demoTestLogic';
import { MarkButton } from '../../../components/MarkControls';
import React, { useState } from 'react';
import P from '@hero/ui-kit/typography/P';
import Tag from '@hero/ui-kit/components/Tag';
import useTestFastForwardDrip from '../../../api/useTestFastForwardDrip';
import useTestResetDrip from '../../../api/useTestResetDrip';
import { useNotifications } from '../../../../../context/notification';
import { extractDripErrorMessage } from '../dripFormUtils';
import DemoModeBanner from './DemoModeBanner';
import DemoProfilePicker from './DemoProfilePicker';
import DemoInstanceRow from './DemoInstanceRow';
import {
    applyFastForwardResult,
    clearRowErrors,
    dripHasRunnableVersion,
    markInstancesReset,
    markRowErrors,
    pendingDraftBlocksTest
} from './demoTestLogic';
import type { AxiosError } from 'axios';
import type { DemoDripInstance, DripDetail } from '../../../types';
type Props = {
    drip: DripDetail;
    instances: DemoDripInstance[];
    onInstancesChange: React.Dispatch<React.SetStateAction<DemoDripInstance[]>>;
};
const withoutId = (ids: Set<number>, id: number): Set<number> => {
    if (!ids.has(id)) return ids;
    const next = new Set(ids);
    next.delete(id);
    return next;
};
const withId = (ids: Set<number>, id: number): Set<number> => {
    const next = new Set(ids);
    next.add(id);
    return next;
};
const Test: React.FC<Props> = ({ drip, instances, onInstancesChange: setInstances }) => {
    const [pickerOpen, setPickerOpen] = useState(false);
    // Keyed sets, not single ids — concurrent fast-forward/reset calls on
    // different rows must not clobber each other's pending marker. Reset is
    // keyed by profile_id (it can affect several rows for that profile);
    // fast-forward is keyed by drip_instance_id (it only ever touches one).
    const [pendingFastForwardIds, setPendingFastForwardIds] = useState<Set<number>>(new Set());
    const [pendingResetProfileIds, setPendingResetProfileIds] = useState<Set<number>>(new Set());
    // One map, keyed by drip_instance_id, for whichever action last touched
    // a row — fast-forward and reset used to keep separate maps, so a stale
    // error from one action could outlive or outrank a fresh outcome from
    // the other on the same row.
    const [rowErrors, setRowErrors] = useState<Map<number, string>>(new Map());
    const fastForward = useTestFastForwardDrip();
    const reset = useTestResetDrip();
    const { addNotification } = useNotifications();
    const runnable = dripHasRunnableVersion(drip);
    const draftBlocked = pendingDraftBlocksTest(drip);
    const isProfileFastForwarding = (profileId: number): boolean =>
        instances.some(
            (i) => i.profile_id === profileId && pendingFastForwardIds.has(i.drip_instance_id)
        );
    const handleEnrolled = (instance: DemoDripInstance) => {
        setInstances((prev) => [...prev, instance]);
        addNotification({
            type: 'success',
            message: `Enrolled instance ${instance.drip_instance_id}.`
        });
        setPickerOpen(false);
    };
    // useMutation's single shared observer overwrites its stored callbacks
    // on every .mutate() call — a second concurrent call can silently steal
    // the first call's onSuccess/onSettled. mutateAsync sidesteps this: its
    // returned promise is tied to that specific call's own execution, so
    // handling it here (rather than via {onSuccess} options) stays correct
    // no matter how many calls overlap.
    const handleFastForward = async (instance: DemoDripInstance) => {
        const { drip_instance_id, profile_id } = instance;
        if (pendingFastForwardIds.has(drip_instance_id)) return;
        if (pendingResetProfileIds.has(profile_id)) return;
        setPendingFastForwardIds((prev) => withId(prev, drip_instance_id));
        setRowErrors((prev) => clearRowErrors(prev, [drip_instance_id]));
        try {
            const response = await fastForward.mutateAsync({
                drip_instance_id,
                drip_id: drip.drip_id,
                profile_id
            });
            setInstances((prev) => applyFastForwardResult(prev, response.data));
            addNotification({
                type: 'success',
                message: 'SendJob due_at set to now. Cron will pick it up on the next tick.'
            });
        } catch (err) {
            const message = extractDripErrorMessage(err as AxiosError);
            setRowErrors((prev) => markRowErrors(prev, [drip_instance_id], message));
            addNotification({ type: 'error', message: `Fast-forward failed: ${message}` });
        } finally {
            setPendingFastForwardIds((prev) => withoutId(prev, drip_instance_id));
        }
    };

    const handleResetProfile = async (profile_id: number): Promise<boolean> => {
        if (pendingResetProfileIds.has(profile_id)) return false;
        if (isProfileFastForwarding(profile_id)) return false;

        // Reset can affect every instance for this profile — its error
        // state must clear/set on all of them, not just the row clicked.
        const profileInstanceIds = instances
            .filter((i) => i.profile_id === profile_id)
            .map((i) => i.drip_instance_id);
        setPendingResetProfileIds((prev) => withId(prev, profile_id));
        setRowErrors((prev) => clearRowErrors(prev, profileInstanceIds));
        try {
            const response = await reset.mutateAsync({ drip_id: drip.drip_id, profile_id });
            setInstances((prev) => markInstancesReset(prev, profile_id));
            addNotification({
                type: 'success',
                message: `Reset ${response.data.deleted_instances} instance(s) for this profile in this drip.`
            });
            return true;
        } catch (err) {
            const message = extractDripErrorMessage(err as AxiosError);
            setRowErrors((prev) => markRowErrors(prev, profileInstanceIds, message));
            addNotification({ type: 'error', message: `Reset failed: ${message}` });
            return false;
        } finally {
            setPendingResetProfileIds((prev) => withoutId(prev, profile_id));
        }
    };
    return (
        <div>
            <DemoModeBanner />
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.8rem',
                    marginBottom: '1.2rem'
                }}
            >
                <MarkButton
                    type="button"
                    onClick={() => setPickerOpen(true)}
                    disabled={!runnable}
                    title={!runnable ? 'This drip has no steps to run yet.' : undefined}
                >
                    Enroll demo profile
                </MarkButton>
                {draftBlocked && (
                    <Tag type="warn" size="small">
                        Testing the pending draft is not supported by the current endpoint. Deferred
                        BE work.
                    </Tag>
                )}
            </div>
            {instances.length === 0 ? (
                <P
                    noDefaultMargin
                    size="small"
                    styles={{
                        color: 'var(--mark-muted)'
                    }}
                >
                    No demo instances enrolled yet on this page.
                </P>
            ) : (
                <div
                    style={{
                        overflowX: 'auto'
                    }}
                    className={layout.tableScroll}
                >
                    <Table<DemoDripInstance>
                        columns={[
                            { colKey: 'drip_instance_id', name: 'Instance' },
                            { colKey: 'profile_id', name: 'Profile' },
                            {
                                colKey: 'status',
                                name: 'Status',
                                render: (instance) => (
                                    <>
                                        {instance.status}{' '}
                                        {instance.reset && (
                                            <Tag type="warn" size="small">
                                                reset
                                            </Tag>
                                        )}
                                    </>
                                )
                            },
                            {
                                colKey: 'enrolled_at',
                                name: 'Enrolled',
                                render: (instance) =>
                                    new Date(instance.enrolled_at).toLocaleTimeString()
                            },
                            {
                                colKey: 'current_step',
                                name: 'Current step',
                                render: (instance) => instance.current_step ?? '—'
                            },
                            {
                                colKey: 'due_at',
                                name: 'Due',
                                render: (instance) => formatDueAt(instance.due_at)
                            },
                            {
                                colKey: 'actions',
                                name: 'Actions',
                                render: (instance) => {
                                    const fastForwardPending = pendingFastForwardIds.has(
                                        instance.drip_instance_id
                                    );
                                    const resetPending = pendingResetProfileIds.has(
                                        instance.profile_id
                                    );
                                    return (
                                        <DemoInstanceRow
                                            instance={instance}
                                            onFastForward={handleFastForward}
                                            onReset={(instance) => handleResetProfile(instance.profile_id)}
                                            fastForwardPending={fastForwardPending}
                                            fastForwardBlocked={
                                                !fastForwardPending &&
                                                pendingResetProfileIds.has(instance.profile_id)
                                            }
                                            resetPending={resetPending}
                                            resetBlocked={
                                                !resetPending &&
                                                isProfileFastForwarding(instance.profile_id)
                                            }
                                            error={rowErrors.get(instance.drip_instance_id) ?? null}
                                        />
                                    );
                                }
                            }
                        ]}
                        data={instances}
                        rowKey="drip_instance_id"
                    />
                </div>
            )}
            {pickerOpen && (
                <DemoProfilePicker
                    drip={drip}
                    onClose={() => setPickerOpen(false)}
                    onEnrolled={handleEnrolled}
                    resetPendingProfileIds={pendingResetProfileIds}
                    onResetProfile={handleResetProfile}
                    fastForwardPendingProfileIds={
                        new Set(
                            instances
                                .filter((i) => pendingFastForwardIds.has(i.drip_instance_id))
                                .map((i) => i.profile_id)
                        )
                    }
                />
            )}
        </div>
    );
};
export default Test;
